// 批次 23（#25，Gitea issue #25）：新增共用表單 modal（form-template-common-form-template，
// origin formTemplate/commonFormTemplate.jsp 353 行；#dialogModal，:269-353）。
// 兩段：1. 選取表單來源（組織→職類→科部→用途→表單，全 required 級聯 :213-241）＋
// 2. 設定要使用的醫院及職類（targetOrgId/targetJobTypeId，:293-324）。
// doInsert（:140-176）：dialogForm.valid() → 目的醫院錯誤！→ 目的職類錯誤！→ 來源表單錯誤！
// （showAlertDialog 逐檢查早退）→ insertCommonFormTemplateAjax（para 只帶 targetOrgId/
// targetJobTypeId/formTemplateId 三鍵 :168-172）→ hide modal → doQuery → showInfoDialog(desc)。
//
// 誠實標註（D23-x）：
//   - 級聯（setJobTypeElement/setDepartmentElement/setFormTemplateList ajax）→ 選項池平列
//     （B2-1 先例）；來源表單池＝FORM_TEMPLATE_LIST_SEED 名稱，依用途過濾（legacy 由
//     setFormTemplateList 帶 purpose 過濾；雛形在畫面層過濾【雛形值待實機校準】）。
//   - payload 帶契約 modal 7 欄（C5-h 全帶；legacy para 只送 3 鍵——差異登記 D23-8）。
//   - 存檔成功 info＝resp.desc（server 回應）→「新增成功【雛形值待實機校準】」。
//   - required inline＝『必填欄位』（批次 14 先例；legacy jquery.validate 訊息）。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { useDataSource } from '@/mocks';
import { legacyAlertFallback } from '../templates/page-behaviors';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_JOB_TYPE_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
  FORM_TEMPLATE_LIST_SEED,
} from '@/mocks/resource-rules';

export interface CommonFormTemplateAddModalProps {
  readonly pageId: string;
  /** 'create'＝新增（showAddModal）；本頁無修改入口（legacy 只有新增；hook 介面保留）。 */
  readonly mode?: 'create' | 'edit';
  readonly onClose: () => void;
  readonly onClosed: () => void;
}

export function CommonFormTemplateAddModal({
  pageId,
  onClose,
  onClosed,
}: CommonFormTemplateAddModalProps): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [values, setValues] = useState<Record<string, string>>({
    organizationId: ACCOUNT_ORGANIZATION_OPTIONS[0]!.value,
    jobTypeId: ACCOUNT_JOB_TYPE_OPTIONS[0]!.value,
    departmentId: ACCOUNT_DEPARTMENT_OPTIONS[0]!.value,
    purpose: 'plan',
    formTemplateId: '',
    targetOrgId: '',
    targetJobTypeId: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  function setValue(name: string, next: string): void {
    setValues((current) => ({ ...current, [name]: next }));
  }

  /** 用途過濾來源表單池（setFormTemplateList 的 purpose 參數語意；畫面層過濾＝D23-7）。 */
  const templatePool = FORM_TEMPLATE_LIST_SEED.filter(
    (row) => values.purpose === '' || String(row['purpose'] ?? '') === values.purpose,
  );

  async function doInsert(): Promise<void> {
    // dialogForm.valid()（:141）：required 欄位 inline（批次 14『必填欄位』文案先例）。
    // 來源段 4 欄 legacy 由級聯自動預選（:95-96 trigger('change')）；清空才會擋。
    // 目的段 3 欄（targetOrgId/targetJobTypeId/formTemplateId）legacy 以 -1 哨兵過 valid()
    //（setJobTypeElement 加 <option value="-1">，formFieldsConfig.js 同款）、由下方自訂
    // 檢查鏉攔（:145-164）——雛形以 '' 等價哨兵，不自訂 inline。
    const errors: Record<string, string> = {};
    for (const name of ['organizationId', 'jobTypeId', 'departmentId', 'purpose']) {
      if (values[name] === undefined || values[name] === '') {
        errors[name] = '必填欄位';
      }
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    // 自訂檢查鏉（doInsert :145-164；順序照 legacy：目的醫院→目的職類→來源表單；
    // legacy 哨兵 -1/空→錯誤，雛形以 '' 等價）。
    if (values['targetOrgId'] === undefined || values['targetOrgId'] === '' || Number(values['targetOrgId']) <= 0) {
      await dialogs.alert('目的醫院錯誤！');
      return;
    }
    if (values['targetJobTypeId'] === undefined || values['targetJobTypeId'] === '' || Number(values['targetJobTypeId']) <= 0) {
      await dialogs.alert('目的職類錯誤！');
      return;
    }
    if (values['formTemplateId'] === undefined || values['formTemplateId'] === '' || Number(values['formTemplateId']) <= 0) {
      await dialogs.alert('來源表單錯誤！');
      return;
    }
    setBusy(true);
    try {
      // formTemplate/insertCommonFormTemplateAjax（:166-176）；C5-h：payload 契約 modal 欄全帶（D23-8）。
      await dataSource.create(pageId, { ...values });
      onClose();
      onClosed();
      await dialogs.info('新增成功【雛形值待實機校準】');
    } catch (cause: unknown) {
      // legacy else 走 showAlertDialog(desc)（:168-172 對照）；mock 失敗＝欄位/訊息 alert。
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  const sourceFields: readonly { name: string; label: string; options: readonly { value: string; label: string }[] }[] = [
    { name: 'organizationId', label: '醫院：', options: ACCOUNT_ORGANIZATION_OPTIONS },
    { name: 'jobTypeId', label: '職類：', options: ACCOUNT_JOB_TYPE_OPTIONS },
    { name: 'departmentId', label: '科部：', options: ACCOUNT_DEPARTMENT_OPTIONS },
    { name: 'targetOrgId', label: '目的醫院：', options: ACCOUNT_ORGANIZATION_OPTIONS },
    { name: 'targetJobTypeId', label: '目的職類：', options: ACCOUNT_JOB_TYPE_OPTIONS },
  ];

  return (
    <ModalFrame
      uiId={`${pageId}-common-template-dialog`}
      open={true}
      title="新增共用表單" // commonFormTemplate.jsp:253
      onClose={onClose}
      footer={
        <>
          <Button uiId={`${pageId}-common-template-dialog-cancel`} variant="default" size="sm" type="button" disabled={busy} onClick={onClose}>
            取消
          </Button>
          <Button
            uiId={`${pageId}-common-template-dialog-save`}
            variant="primary"
            size="sm"
            type="button"
            disabled={busy}
            // C5-j：新增真呼叫 dataSource.create（doInsert :140-176 → insertCommonFormTemplateAjax）。
            dataEffect={`create:${pageId}`}
            onClick={() => {
              void doInsert();
            }}
          >
            存檔
          </Button>
        </>
      }
    >
      <div data-ui-id={`${pageId}-common-template-dialog-form`} className="space-y-3">
        {/* 1. 選取表單來源（:216 bg-info 標題）。 */}
        <div data-ui-id={`${pageId}-common-template-dialog-section-source`} className="text-sm font-bold">
          1. 選取表單來源
        </div>
        {sourceFields.slice(0, 3).map((field) => (
          <div key={field.name} className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-common-template-dialog-${field.name}`}>
              <span className="text-danger">*</span>
              {field.label}
            </label>
            <div className="flex-1">
              <Select
                uiId={`${pageId}-common-template-dialog-${field.name}`}
                name={field.name}
                value={values[field.name] ?? ''}
                onChange={(next) => setValue(field.name, next)}
                options={field.options}
                placeholder="請選擇"
              />
              {fieldErrors[field.name] === undefined ? null : (
                <div data-ui-id={`${pageId}-common-template-dialog-error-${field.name}`} className="text-danger text-xs">
                  {fieldErrors[field.name]}
                </div>
              )}
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-common-template-dialog-purpose`}>
            <span className="text-danger">*</span>
            用途：
          </label>
          <div className="flex-1">
            <Select
              uiId={`${pageId}-common-template-dialog-purpose`}
              name="purpose"
              value={values['purpose'] ?? ''}
              onChange={(next) => {
                setValue('purpose', next);
                setValue('formTemplateId', '');
              }}
              options={[
                { value: 'plan', label: '訓練計畫' },
                { value: 'class', label: '課程' },
              ]}
            />
            {fieldErrors.purpose === undefined ? null : (
              <div data-ui-id={`${pageId}-common-template-dialog-error-purpose`} className="text-danger text-xs">
                {fieldErrors.purpose}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-common-template-dialog-form-template-id`}>
            <span className="text-danger">*</span>
            表單：
          </label>
          <div className="flex-1">
            <Select
              uiId={`${pageId}-common-template-dialog-form-template-id`}
              name="formTemplateId"
              value={values['formTemplateId'] ?? ''}
              onChange={(next) => setValue('formTemplateId', next)}
              options={templatePool.map((row) => ({ value: String(row['id'] ?? ''), label: String(row['name'] ?? '') }))}
              placeholder="請選擇"
            />
            {fieldErrors.formTemplateId === undefined ? null : (
              <div data-ui-id={`${pageId}-common-template-dialog-error-form-template-id`} className="text-danger text-xs">
                {fieldErrors.formTemplateId}
              </div>
            )}
          </div>
        </div>
        {/* 2. 設定要使用的醫院及職類（:294 bg-warning 標題）。 */}
        <div data-ui-id={`${pageId}-common-template-dialog-section-target`} className="text-sm font-bold">
          2. 設定要使用的醫院及職類
        </div>
        {sourceFields.slice(3).map((field) => (
          <div key={field.name} className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-common-template-dialog-${field.name}`}>
              <span className="text-danger">*</span>
              {field.label}
            </label>
            <div className="flex-1">
              <Select
                uiId={`${pageId}-common-template-dialog-${field.name}`}
                name={field.name}
                value={values[field.name] ?? ''}
                onChange={(next) => setValue(field.name, next)}
                options={field.options}
                placeholder="請選擇"
              />
              {fieldErrors[field.name] === undefined ? null : (
                <div data-ui-id={`${pageId}-common-template-dialog-error-${field.name}`} className="text-danger text-xs">
                  {fieldErrors[field.name]}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </ModalFrame>
  );
}
